/**
 * What a subagent is doing now, relayed by the turn that waits on it. OpenCode's live event
 * stream carries every session in the sandbox, so the turn's observer already receives the
 * events of the lead's subagents. It relays none of them as they are: each child's events
 * become one small event whenever what the child does changes, naming the child, which the
 * transcript matches to the lead's call. Pure and client-safe: the page reads the event's shape
 * and type from here, and the turn hands its sandbox client in.
 */
import type { SessionMessageInfo } from '@opencode/client/promise';

import type { OpenCodeClient } from './sandbox/opencode-http-client';
import type { ToolStepKind } from './tool-steps';

import { isToolStepKind, toolStepDetail, toolStepKind } from './tool-steps';

import { getRecord } from '@/lib/utils/unknown';

/**
 * What a subagent does now: runs a tool on something, thinks, or writes its reply; idle once its
 * run ended, until it runs again.
 */
export type SubagentActivity =
  | { kind: 'tool'; tool: ToolStepKind; detail: string }
  | { kind: 'thinking' }
  | { kind: 'writing' }
  | { kind: 'idle' };

/** What a subagent that runs does: any activity but idle. */
export type SubagentWork = Exclude<SubagentActivity, { kind: 'idle' }>;

/** The agent event type a turn relays a subagent's activity as. */
export const SUBAGENT_ACTIVITY_EVENT = 'subagent.activity';

/**
 * What one of the lead's subagents does, named by its child session. `title` is the child's
 * title once the turn read it, which OpenCode gives a new child from the call's description: the
 * page matches a call that never learned its child by it.
 */
export type SubagentActivityEvent = {
  sessionID: string;
  title?: string;
  activity: SubagentActivity;
};

/** A tool's detail goes out as one line of at most this many characters. */
const DETAIL_MAX_CHARS = 200;

function activityOf(value: unknown): SubagentActivity | undefined {
  const record = getRecord(value);
  switch (record?.kind) {
    case 'thinking':
      return { kind: 'thinking' };
    case 'writing':
      return { kind: 'writing' };
    case 'idle':
      return { kind: 'idle' };
    case 'tool': {
      const { tool, detail } = record;
      return typeof tool === 'string' && isToolStepKind(tool) && typeof detail === 'string'
        ? { kind: 'tool', tool, detail }
        : undefined;
    }
    default:
      return undefined;
  }
}

/**
 * A relayed event's data as an activity event, or undefined when it does not read as one. The
 * page reads events another version of this code may have written.
 */
export function subagentActivityEventOf(data: unknown): SubagentActivityEvent | undefined {
  const record = getRecord(data);
  const activity = activityOf(record?.activity);
  const { sessionID, title } = record ?? {};
  if (activity === undefined || typeof sessionID !== 'string') {
    return undefined;
  }
  return typeof title === 'string' ? { sessionID, title, activity } : { sessionID, activity };
}

/**
 * The child a call that never learned it runs in, by its description, which OpenCode titles a new
 * child with: the first candidate titled with it, else the first whose title starts with it,
 * skipping children another call has. Candidates come in the order to prefer them. Ported from
 * `taskSession` in OpenCode v2.0.16 packages/session-ui/src/tools/tool-renderer.tsx (MIT,
 * Copyright (c) 2025 opencode).
 */
export function childTitled(
  description: string,
  candidates: readonly { id: string; title?: string }[],
  taken: ReadonlySet<string>,
): string | undefined {
  if (description === '') {
    return undefined;
  }
  const titled = candidates.filter(
    (child) => !taken.has(child.id) && child.title?.startsWith(description) === true,
  );
  return (titled.find((child) => child.title === description) ?? titled[0])?.id;
}

/** What the relay reads of the sandbox's OpenCode. Any read may fail. */
export type SubagentActivityReads = {
  /** The sessions OpenCode runs now. */
  activeSessionIds: () => Promise<readonly string[]>;
  /** A session's parent and title. */
  session: (id: string) => Promise<{ parentID?: string; title?: string }>;
  /** A session's newest saved message; undefined while it has none. */
  newestMessage: (id: string) => Promise<SessionMessageInfo | undefined>;
};

/**
 * What the relay reads of the sandbox, through the client the turn uses when it reads:
 * recovering the sandbox replaces it. Each read ends with `signal()`.
 */
export function openCodeSubagentReads(
  client: () => OpenCodeClient,
  signal: () => AbortSignal,
): SubagentActivityReads {
  return {
    activeSessionIds: async () => Object.keys(await client().session.active({ signal: signal() })),
    session: (id) => client().session.get(id, { signal: signal() }),
    // What a child does now is its newest message's.
    newestMessage: async (id) =>
      (await client().message.list(id, { order: 'desc', limit: 1, signal: signal() })).data.at(0),
  };
}

/** One of a session's live events, as the relay keeps it. */
type SessionEvent = { type: string; data: Record<string, unknown> };

/**
 * A session other than the lead's whose events the relay follows: one of the lead's children, or
 * one it may be until a read of it says.
 */
type MetSession = {
  /** Whether it is one of the lead's subagents: unknown, being read, or known to be. */
  whose: undefined | Promise<boolean> | 'child';
  /** Its title, from that read. */
  title?: string;
  /** Tool names by call (`assistantMessageID:id`), from `input.started`: `called` does not repeat them. */
  toolNames: Map<string, string>;
  /** Tools called and not settled, newest last. */
  running: Map<string, SubagentWork>;
  /** What it does while it runs no tool, as its newest event or a read of its messages said. */
  phase?: 'thinking' | 'writing' | 'idle';
  /** What the last relayed event said. */
  relayed?: SubagentActivity;
};

/** The first line with text, trimmed, cut to `DETAIL_MAX_CHARS`. */
function detailLine(text: string): string {
  const line =
    text
      .split('\n')
      .find((candidate) => candidate.trim().length > 0)
      ?.trim() ?? '';
  return line.length > DETAIL_MAX_CHARS ? `${line.slice(0, DETAIL_MAX_CHARS - 1)}…` : line;
}

function sameActivity(a: SubagentActivity, b: SubagentActivity): boolean {
  if (a.kind === 'tool' && b.kind === 'tool') {
    return a.tool === b.tool && a.detail === b.detail;
  }
  return a.kind === b.kind;
}

function toolWork(name: string, input: unknown): SubagentWork {
  return { kind: 'tool', tool: toolStepKind(name), detail: toolStepDetail(name, input).detail };
}

/** What a session does now: the newest tool it runs, else what it does between tools. */
function currentActivity(met: MetSession): SubagentActivity | undefined {
  const tool = [...met.running.values()].at(-1);
  return tool ?? (met.phase === undefined ? undefined : { kind: met.phase });
}

/** Forgets the tools the session was known to run, which its run's end or a read replaces. */
function forgetTools(met: MetSession): void {
  met.toolNames.clear();
  met.running.clear();
}

/**
 * Applies one of the session's events to what it does: a called tool runs until it settles, and
 * between tools a settled tool, a new step or reasoning is thinking, text is writing, and its
 * run's end is idle. Says whether the event is one of these; other events change nothing.
 */
function applyEvent(met: MetSession, { type, data }: SessionEvent): boolean {
  const key =
    typeof data.assistantMessageID === 'string' && typeof data.id === 'string'
      ? `${data.assistantMessageID}:${data.id}`
      : undefined;
  switch (type) {
    case 'session.tool.input.started':
      if (key !== undefined && typeof data.name === 'string') {
        met.toolNames.set(key, data.name);
      }
      return true;
    case 'session.tool.called': {
      const name = key === undefined ? undefined : met.toolNames.get(key);
      if (key !== undefined && name !== undefined) {
        met.running.delete(key);
        met.running.set(key, toolWork(name, data.input));
      }
      return true;
    }
    case 'session.tool.success':
    case 'session.tool.failed':
      if (key !== undefined) {
        met.running.delete(key);
        met.toolNames.delete(key);
      }
      met.phase = 'thinking';
      return true;
    case 'session.step.started':
    case 'session.reasoning.started':
      met.phase = 'thinking';
      return true;
    case 'session.text.started':
      met.phase = 'writing';
      return true;
    case 'session.execution.succeeded':
    case 'session.execution.failed':
    case 'session.execution.interrupted':
      forgetTools(met);
      met.phase = 'idle';
      return true;
    default:
      return false;
  }
}

/**
 * Takes what the session's newest saved message says it does: the newest tool it runs, writing
 * while its unfinished reply ends in text, thinking otherwise, and idle once its run ended. A
 * session with nothing saved yet says nothing.
 */
function applySaved(met: MetSession, newest: SessionMessageInfo | undefined): void {
  if (newest === undefined) {
    return;
  }
  forgetTools(met);
  if (newest.type !== 'assistant') {
    met.phase = newest.type === 'idle' ? 'idle' : 'thinking';
    return;
  }
  for (const part of newest.content) {
    if (part.type !== 'tool') {
      continue;
    }
    const key = `${newest.id}:${part.id}`;
    // A tool whose input still streams is named here, for its `called` event to come.
    if (part.state.status === 'streaming' || part.state.status === 'running') {
      met.toolNames.set(key, part.name);
    }
    if (part.state.status === 'running') {
      met.running.set(key, toolWork(part.name, part.state.input));
    }
  }
  const writing = newest.time.completed === undefined && newest.content.at(-1)?.type === 'text';
  met.phase = writing ? 'writing' : 'thinking';
}

export type SubagentActivityRelay = {
  /**
   * Hands the relay one of the turn's live events and the redactor of the observation that heard
   * it: what the relay sends redacts with the newest it was handed. Never throws, and never
   * waits: what it reads of the sandbox comes back later and goes out through `emit` then.
   */
  next: (item: { type: string; data: unknown }, redact: (text: string) => string) => void;
};

/**
 * Follows the lead's subagents on the live stream, which can miss the lead's progress event that
 * names a child: it comes once, and the stream reconnects for every observation. Each connection
 * opens with `server.connected`, so the relay then reads which sessions run, which of them are the
 * lead's children (one read per session) and what each does from its newest saved message, which
 * also covers a command that started before the connection and prints nothing for minutes. A read
 * is older than the events met while it was out, which go again onto what it found. Between
 * reads, a child's events say what it does. A session met on the stream is read once, its events
 * followed meanwhile, and dropped unless it is the lead's child, so side agents and their children
 * are read once and ignored. A child whose run ends, or that a later connection no longer finds
 * running, is idle. `emit` gets an event whenever what a child does changes, with the redactor to
 * redact it with, a tool's detail redacted whole and then cut to its first line: cut first, a
 * secret could no longer match.
 */
export function createSubagentActivityRelay(input: {
  leadSessionId: string;
  reads: SubagentActivityReads;
  emit: (event: SubagentActivityEvent, redact: (text: string) => string) => void;
}): SubagentActivityRelay {
  const { leadSessionId, reads, emit } = input;
  // Each session met, or 'other' once a read said it is not the lead's child: a side agent, or a
  // child of one, whose events are ignored from then on.
  const sessions = new Map<string, MetSession | 'other'>();
  // The redactor of the newest observation that handed the relay an event.
  let newestRedact: ((text: string) => string) | undefined;
  // The events met while a sync runs, which its reads are older than; none between syncs.
  let kept: { id: string; event: SessionEvent }[] | undefined;
  let syncing = false;
  let syncAgain = false;

  const metOf = (id: string): MetSession | 'other' => {
    const known = sessions.get(id);
    if (known !== undefined) {
      return known;
    }
    const met: MetSession = { whose: undefined, toolNames: new Map(), running: new Map() };
    sessions.set(id, met);
    return met;
  };

  /** Sends what a child does now, when that differs from what was last sent of it. */
  const relay = (id: string, met: MetSession) => {
    const now = currentActivity(met);
    // Nothing goes out before an event handed the relay a redactor.
    if (met.whose !== 'child' || now === undefined || newestRedact === undefined) {
      return;
    }
    try {
      const activity =
        now.kind === 'tool' ? { ...now, detail: detailLine(newestRedact(now.detail)) } : now;
      if (met.relayed !== undefined && sameActivity(met.relayed, activity)) {
        return;
      }
      emit(
        met.title === undefined
          ? { sessionID: id, activity }
          : { sessionID: id, title: met.title, activity },
        newestRedact,
      );
      met.relayed = activity;
    } catch {
      // Not sent, so not relayed: the next change tries again.
    }
  };

  /** Whether the session is one of the lead's subagents: read once, then known. */
  const isChild = (id: string, met: MetSession): Promise<boolean> => {
    if (met.whose === 'child') {
      return Promise.resolve(true);
    }
    if (met.whose !== undefined) {
      return met.whose;
    }
    const read = reads.session(id).then((info) => {
      if (info.parentID !== leadSessionId) {
        sessions.set(id, 'other');
        return false;
      }
      met.whose = 'child';
      met.title = info.title;
      // What its events said while the read was out.
      relay(id, met);
      return true;
    });
    met.whose = read;
    // A read that fails is forgotten, so the next event reads again.
    read.catch(() => {
      met.whose = undefined;
    });
    return read;
  };

  /**
   * A read the sync made is older than the events met while it was out: what it found is the
   * base, and the session's events kept since the sync began go again on top.
   */
  const rebase = (id: string, met: MetSession, base: () => void) => {
    base();
    for (const entry of kept ?? []) {
      if (entry.id === id) {
        applyEvent(met, entry.event);
      }
    }
    relay(id, met);
  };

  /** Reads what a running child does from its newest saved message. */
  const readChild = async (id: string) => {
    const met = metOf(id);
    if (met === 'other' || !(await isChild(id, met))) {
      return;
    }
    const newest = await reads.newestMessage(id);
    rebase(id, met, () => applySaved(met, newest));
  };

  /** Reads what runs now, for a connection that may have missed what happened before it. */
  const sync = async () => {
    const followed = [...sessions].flatMap(([id, met]) =>
      met !== 'other' && met.whose === 'child' ? [{ id, met }] : [],
    );
    const active = new Set(await reads.activeSessionIds());
    // A child no longer running ended while no connection heard it; one still running is read.
    for (const { id, met } of followed) {
      if (!active.has(id)) {
        rebase(id, met, () => {
          forgetTools(met);
          met.phase = 'idle';
        });
      }
    }
    await Promise.all(
      [...active]
        .filter((id) => id !== leadSessionId)
        .map((id) =>
          readChild(id).catch(() => {
            // Unread, it is read on its next event or the next connection.
          }),
        ),
    );
  };

  /** One sync at a time; a connection that opens during one gets one more after it. */
  const requestSync = () => {
    if (syncing) {
      syncAgain = true;
      return;
    }
    syncing = true;
    void (async () => {
      do {
        syncAgain = false;
        kept = [];
        await sync().catch(() => undefined);
      } while (syncAgain);
      kept = undefined;
      syncing = false;
    })();
  };

  return {
    next(item, redact) {
      try {
        newestRedact = redact;
        if (item.type === 'server.connected') {
          requestSync();
          return;
        }
        const data = getRecord(item.data);
        const id = data?.sessionID;
        if (data === null || typeof id !== 'string' || id === leadSessionId) {
          return;
        }
        const met = metOf(id);
        if (met === 'other') {
          return;
        }
        const event = { type: item.type, data };
        if (applyEvent(met, event)) {
          kept?.push({ id, event });
        }
        if (met.whose === 'child') {
          relay(id, met);
        } else {
          void isChild(id, met);
        }
      } catch {
        // What a subagent does is a courtesy: nothing here may stop the turn's own events.
      }
    },
  };
}
